import { flushSync } from 'react-dom';

const THEME_SWITCH_DURATION_MS = 1080;
const FALLBACK_PAGE_BG = '#f5f4f2';
const FALLBACK_ACCENT = '#e87272';
const CLEANUP_GRACE_MS = 120;

let themeSwitchInFlight = false;
let activeSession = null;

const prefersReducedMotion = () =>
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);

const isFinitePoint = (value) => Number.isFinite(value);

export const resolveThemeSwitchOrigin = (input) => {
  if (!input || typeof input !== 'object') {
    return null;
  }

  if (isFinitePoint(input.x) && isFinitePoint(input.y)) {
    return { x: input.x, y: input.y };
  }

  const node = input.currentTarget || input.target;
  if (node?.getBoundingClientRect) {
    const rect = node.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    if (isFinitePoint(x) && isFinitePoint(y)) {
      return { x, y };
    }
  }

  return null;
};

const coverageRadius = (x, y) =>
  Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));

/** Matches cubic-bezier(0.22, 1, 0.36, 1) closely enough for the reveal. */
const easeOutQuart = (t) => 1 - (1 - t) ** 4;

const holeMaskImage = (x, y, radius) =>
  `radial-gradient(circle at ${x}px ${y}px, transparent ${radius}px, #000 ${radius + 0.75}px)`;

const applyOnce = (apply) => {
  let applied = false;
  try {
    flushSync(() => {
      apply();
      applied = true;
    });
  } catch {
    if (!applied) {
      apply();
    }
  }
};

const teardownSession = (session) => {
  if (!session || session.finished) {
    return;
  }
  session.finished = true;
  if (session.frameId != null) {
    window.cancelAnimationFrame(session.frameId);
  }
  if (session.timeoutId != null) {
    window.clearTimeout(session.timeoutId);
  }
  window.removeEventListener('pagehide', session.onPageHide);
  session.root?.remove();
  if (activeSession === session) {
    activeSession = null;
    themeSwitchInFlight = false;
  }
};

const runOverlayReveal = ({ x, y, endRadius, previousPageBg, accent, apply }) => {
  const root = document.createElement('div');
  root.setAttribute('data-theme-switch-reveal', 'true');
  root.setAttribute('aria-hidden', 'true');
  root.style.cssText =
    'position:fixed;inset:0;z-index:2147483646;pointer-events:none;contain:strict;';

  const veil = document.createElement('div');
  veil.style.cssText = `position:absolute;inset:0;background:${previousPageBg};`;
  veil.style.webkitMaskImage = holeMaskImage(x, y, 0);
  veil.style.maskImage = holeMaskImage(x, y, 0);
  veil.style.webkitMaskRepeat = 'no-repeat';
  veil.style.maskRepeat = 'no-repeat';

  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;overflow:visible;';

  const ring = document.createElementNS(ns, 'circle');
  ring.setAttribute('cx', String(x));
  ring.setAttribute('cy', String(y));
  ring.setAttribute('r', '0');
  ring.setAttribute('fill', 'none');
  ring.setAttribute('stroke', accent);
  ring.setAttribute('stroke-width', '2');
  ring.setAttribute('vector-effect', 'non-scaling-stroke');
  ring.setAttribute('opacity', '0.85');

  svg.append(ring);
  root.append(veil, svg);
  document.body.appendChild(root);

  const session = {
    finished: false,
    root,
    frameId: null,
    timeoutId: null,
    onPageHide: null,
  };
  session.onPageHide = () => teardownSession(session);
  activeSession = session;
  window.addEventListener('pagehide', session.onPageHide);

  applyOnce(apply);

  const duration = THEME_SWITCH_DURATION_MS;
  const startedAt = performance.now();

  const paint = (radius, progress) => {
    veil.style.webkitMaskImage = holeMaskImage(x, y, radius);
    veil.style.maskImage = holeMaskImage(x, y, radius);
    ring.setAttribute('r', String(radius));
    ring.setAttribute('opacity', String(0.85 * (1 - progress)));
  };

  const tick = (now) => {
    if (session.finished) {
      return;
    }
    const progress = Math.min(1, (now - startedAt) / duration);
    paint(easeOutQuart(progress) * endRadius, progress);
    if (progress >= 1) {
      teardownSession(session);
      return;
    }
    session.frameId = window.requestAnimationFrame(tick);
  };

  session.frameId = window.requestAnimationFrame(tick);
  session.timeoutId = window.setTimeout(
    () => teardownSession(session),
    duration + CLEANUP_GRACE_MS,
  );
};

export const resetThemeSwitchTransitionForTests = () => {
  teardownSession(activeSession);
  themeSwitchInFlight = false;
};

/** Circular reveal from the sun/moon icon; new theme shows through the growing hole. */
export const runThemeSwitchTransition = ({
  origin,
  apply,
  previousPageBg,
  nextPageBg,
  accent,
} = {}) => {
  if (typeof apply !== 'function') {
    return;
  }

  if (typeof document === 'undefined' || !document.body) {
    apply();
    return;
  }

  if (themeSwitchInFlight) {
    return;
  }

  const point = resolveThemeSwitchOrigin(origin);
  if (prefersReducedMotion() || !point) {
    apply();
    return;
  }

  const endRadius = coverageRadius(point.x, point.y);
  if (!isFinitePoint(endRadius) || endRadius <= 0) {
    apply();
    return;
  }

  themeSwitchInFlight = true;
  try {
    runOverlayReveal({
      x: point.x,
      y: point.y,
      endRadius,
      previousPageBg: previousPageBg || nextPageBg || FALLBACK_PAGE_BG,
      accent: accent || FALLBACK_ACCENT,
      apply,
    });
  } catch {
    themeSwitchInFlight = false;
    activeSession = null;
    apply();
  }
};
